import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import ROICalculator from "./ROICalculator";
import kumiaLogo from "./KumIA_Official_Logo_Green.png";
import {
  ArrowRight,
  Zap,
  Star,
  Menu,
  X,
  Lock,
} from "lucide-react";

const NAV_ITEMS = [
  { label: "Inicio", id: "inicio" },
  { label: "Precios", id: "precios" },
  { label: "Ecosistema", id: "features" },
  { label: "Ciclo KumIA", id: "ciclo" },
  { label: "Sobre Nosotros", href: "/sobre-nosotros" },
];

const FuturisticKumiaLanding = ({ onShowSignIn, onShowSignUp }) => {
  const [showROICalculator, setShowROICalculator] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    // Cerrar menú mobile primero
    setMobileMenuOpen(false);

    // Esperar un momento para que el menú se cierre
    setTimeout(() => {
      if (sectionId === "inicio") {
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }

      // Intentar encontrar y hacer scroll al elemento
      const attemptScroll = (attempts = 0) => {
        const element = document.getElementById(sectionId);
        if (element) {
          const navbarHeight = 80;
          const offsetTop = element.offsetTop - navbarHeight;
          window.scrollTo({ top: offsetTop, behavior: "smooth" });
        } else if (attempts < 15) {
          // Reintentar hasta 15 veces (1.5 segundos total)
          setTimeout(() => attemptScroll(attempts + 1), 100);
        } else {
          console.warn(`No se encontró el elemento con id: ${sectionId}`);
        }
      };

      attemptScroll();
    }, 200); // Esperar 200ms para que el menú mobile se cierre
  };

  return (
    <div className="min-h-screen font-body text-kumia-text" style={{ background: 'linear-gradient(180deg, #060B14 0%, #0F1729 100%)' }}>

      {/* ── Navbar ── */}
      <motion.nav
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="fixed top-0 w-full z-50 px-4 py-3 transition-all duration-300"
        style={{
          background: scrollY > 50
            ? "rgba(6, 11, 20, 0.92)"
            : "rgba(6, 11, 20, 0.75)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderBottom: `1px solid ${scrollY > 50 ? "rgba(122, 201, 67, 0.12)" : "rgba(122, 201, 67, 0.06)"}`,
          boxShadow: scrollY > 50 ? "0 8px 32px rgba(0,0,0,0.4)" : "none",
        }}
      >
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          {/* Logo */}
          <motion.div className="flex items-center space-x-1" whileHover={{ scale: 1.04 }}>
            <img
              src={kumiaLogo}
              alt="KumIA - Ecosistema Operativo para Restaurantes"
              className="w-14 h-10 object-contain"
              width={56}
              height={40}
              style={{ filter: "saturate(1.2) brightness(1.1)" }}
            />
            <span className="text-xl font-display font-bold">
              <span className="text-kumia-text">Kum</span>
              <span className="text-kumia-green">IA</span>
            </span>
          </motion.div>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center space-x-8">
            {NAV_ITEMS.map((item) =>
              item.href ? (
                <Link
                  key={item.href}
                  to={item.href}
                  className="text-kumia-text-secondary hover:text-kumia-green transition-colors font-medium text-sm"
                >
                  {item.label}
                </Link>
              ) : (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="text-kumia-text-secondary hover:text-kumia-green transition-colors font-medium text-sm"
                >
                  {item.label}
                </button>
              )
            )}
          </div>

          {/* Auth */}
          <div className="hidden md:flex items-center space-x-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onShowSignUp}
              className="btn-glow bg-gradient-to-r from-kumia-green to-green-600 px-5 py-2 rounded-full font-display font-semibold text-sm text-white"
            >
              Crear Cuenta
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onShowSignIn}
              className="text-kumia-text-secondary hover:text-kumia-green transition-colors font-semibold text-sm"
            >
              Iniciar Sesión
            </motion.button>
          </div>

          {/* Mobile Toggle */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 text-kumia-text-secondary hover:text-kumia-green transition-colors"
            aria-label="Menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4 lg:hidden"
            >
              <div
                className="flex flex-col space-y-3 p-4 rounded-xl"
                style={{
                  background: "rgba(19, 27, 46, 0.95)",
                  border: "1px solid rgba(122, 201, 67, 0.1)",
                  backdropFilter: "blur(12px)",
                }}
              >
                {NAV_ITEMS.map((item) =>
                  item.href ? (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="text-kumia-text-secondary hover:text-kumia-green transition-colors font-medium text-left text-sm py-1"
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      className="text-kumia-text-secondary hover:text-kumia-green transition-colors font-medium text-left text-sm py-1"
                    >
                      {item.label}
                    </button>
                  )
                )}
                <div className="pt-3 border-t border-white/5 flex flex-col space-y-2">
                  <button
                    onClick={() => { onShowSignUp(); setMobileMenuOpen(false); }}
                    className="w-full bg-gradient-to-r from-kumia-green to-green-600 px-4 py-2.5 rounded-lg font-display font-semibold text-sm text-white"
                  >
                    Crear Cuenta
                  </button>
                  <button
                    onClick={() => { onShowSignIn(); setMobileMenuOpen(false); }}
                    className="w-full text-kumia-text-secondary hover:text-kumia-green transition-colors font-semibold text-sm py-2"
                  >
                    Iniciar Sesión
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>

      {/* ── Hero Section ── */}
      <section id="inicio" className="relative min-h-screen flex flex-col items-center justify-center px-4 pt-24 pb-16">

        {/* Background: restaurant video */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            className="absolute w-full h-full object-cover"
            style={{ opacity: 0.35 }}
          >
            <source src="/bartender-mixing-1080.mp4" type="video/mp4" />
          </video>
          <div
            className="absolute inset-0"
            style={{
              background: "linear-gradient(180deg, rgba(6,11,20,0.5) 0%, rgba(6,11,20,0.15) 40%, rgba(6,11,20,0.6) 100%)",
            }}
          />
        </div>

        {/* Background: dot grid with parallax */}
        <div
          className="absolute inset-0 dot-grid pointer-events-none will-change-transform"
          style={{ transform: `translateY(${scrollY * 0.15}px)`, opacity: 0.5 }}
        />

        {/* Background: radial vignette */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "radial-gradient(ellipse 70% 50% at 50% 45%, rgba(122, 201, 67, 0.04) 0%, transparent 70%)",
          }}
        />

        {/* Floating orbs */}
        <div
          className="absolute top-[15%] left-[10%] w-[420px] h-[420px] rounded-full animate-float-slow pointer-events-none"
          style={{
            background: "radial-gradient(circle, rgba(122, 201, 67, 0.08) 0%, transparent 70%)",
            filter: "blur(60px)",
          }}
        />
        <div
          className="absolute bottom-[10%] right-[8%] w-[350px] h-[350px] rounded-full animate-float-slower pointer-events-none"
          style={{
            background: "radial-gradient(circle, rgba(251, 191, 36, 0.06) 0%, transparent 70%)",
            filter: "blur(60px)",
          }}
        />
        <div
          className="absolute top-[55%] right-[25%] w-[250px] h-[250px] rounded-full animate-float-slow pointer-events-none"
          style={{
            background: "radial-gradient(circle, rgba(122, 201, 67, 0.05) 0%, transparent 70%)",
            filter: "blur(50px)",
            animationDelay: "-8s",
          }}
        />

        {/* Content */}
        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            {/* Title - improved contrast */}
            <h1 className="font-display font-extrabold text-3xl md:text-6xl lg:text-7xl leading-[1.1] tracking-tight mb-6">
              <span className="text-kumia-text-secondary">El </span>
              <span className="text-kumia-text">Ecosistema Operativo</span>
              <br className="hidden sm:block" />
              <span className="text-kumia-text-secondary"> que </span>
              <span className="text-gradient-animate">Gamifica</span>
              <span className="text-kumia-text-secondary"> tu Restaurante.</span>
            </h1>

            {/* Badge: Accion Digital = Recompensa */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="flex justify-center mb-8"
            >
              <div
                className="badge-shimmer inline-flex items-center gap-3 px-5 py-2.5 rounded-full"
                style={{
                  background: "linear-gradient(135deg, rgba(122, 201, 67, 0.08) 0%, rgba(251, 191, 36, 0.06) 100%)",
                  border: "1px solid rgba(122, 201, 67, 0.18)",
                  boxShadow: "0 0 30px rgba(122, 201, 67, 0.06), inset 0 1px 0 rgba(255,255,255,0.03)",
                }}
              >
                <span className="flex items-center gap-1.5">
                  <Zap className="w-4 h-4 text-kumia-green" strokeWidth={2.5} />
                  <span className="text-kumia-green font-display font-semibold text-sm">Acción Digital</span>
                </span>
                <span
                  className="font-display font-bold text-base"
                  style={{
                    background: "linear-gradient(135deg, #7AC943, #FBBF24)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  =
                </span>
                <span className="flex items-center gap-1.5">
                  <Star className="w-4 h-4 text-kumia-amber" fill="#FBBF24" strokeWidth={2} />
                  <span className="text-kumia-amber font-display font-semibold text-sm">Recompensa</span>
                </span>
              </div>
            </motion.div>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.7 }}
              className="text-base md:text-xl text-kumia-text-secondary max-w-3xl mx-auto mb-10 leading-relaxed font-body px-2 md:px-0"
            >
              Digitaliza tu local con costo fijo{" "}
              <span className="text-kumia-green font-semibold">$0</span>.
              Optimizamos tu operación con{" "}
              <span className="text-kumia-text font-semibold">Kitchen Matrix</span>{" "}
              mientras tus clientes canjean{" "}
              <span className="text-kumia-amber font-semibold">recompensas directamente de tu carta</span>{" "}
              jugando, dejando reviews y disfrutando de la experiencia única de la gamificación KumIA.
              El primer{" "}
              <span className="text-kumia-text font-semibold">SaaS On-Premise</span>{" "}
              diseñado y personalizado para ti, en donde solo pagas cuando lo usen tus clientes.
            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16"
            >
              <motion.a
                href="https://dashboard.kumia.app/"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
                className="btn-glow group bg-gradient-to-r from-kumia-green to-green-600 text-white px-8 py-4 rounded-full text-lg font-display font-bold flex items-center gap-2 shadow-lg"
                style={{ boxShadow: "0 4px 24px rgba(122, 201, 67, 0.25)" }}
              >
                Comenzar Gratis
                <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
              </motion.a>
              <motion.a
                href="/diagnostico"
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
                className="group flex items-center gap-2 px-8 py-4 rounded-full text-lg font-display font-semibold text-kumia-green hover:text-white transition-colors"
                style={{
                  border: "1.5px solid rgba(122, 201, 67, 0.45)",
                  background: "rgba(122, 201, 67, 0.06)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(122, 201, 67, 0.18)";
                  e.currentTarget.style.borderColor = "rgba(122, 201, 67, 0.7)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(122, 201, 67, 0.06)";
                  e.currentTarget.style.borderColor = "rgba(122, 201, 67, 0.45)";
                }}
              >
                <Lock className="w-5 h-5" strokeWidth={2.2} />
                Quiero mi precio Founder lifetime
                <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
              </motion.a>
            </motion.div>

            {/* Product Mockup — Dashboard + 2 phones overlapping */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.9, ease: "easeOut" }}
              className="relative max-w-5xl mx-auto"
            >
              <div className="mockup-glow" />

              <div className="relative">
                {/* Dashboard MacBook — full width */}
                <motion.div
                  className="relative z-10 w-full animate-float-gentle"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.1, duration: 0.8 }}
                >
                  {/* Dark background behind MacBook screen area */}
                  <div
                    className="absolute"
                    style={{
                      top: "1.5%",
                      left: "16.5%",
                      width: "68%",
                      height: "87%",
                      background: "#000000",
                      borderRadius: "8px 8px 0 0",
                    }}
                  />
                  <picture>
                    <source srcSet="/Screenshot-Dashboard-v2-nobg.webp" type="image/webp" />
                    <img
                      src="/Screenshot-Dashboard-v2-nobg.png"
                      alt="KumIA Dashboard - Kitchen Matrix panel de control en tiempo real para restaurantes"
                      className="relative w-full h-auto object-contain"
                      fetchpriority="high"
                      width={1330}
                      height={676}
                      style={{
                        filter: "drop-shadow(0 25px 50px rgba(0, 0, 0, 0.3))",
                      }}
                    />
                  </picture>
                </motion.div>

                {/* Phone mockups — overlapping bottom-right of dashboard */}
                <div className="absolute bottom-[-10%] right-[0%] md:right-[2%] z-20 flex gap-2 md:gap-3">
                  {/* Phone 1: Restaurant */}
                  <motion.div
                    className="w-[110px] md:w-[170px] lg:w-[195px] rounded-2xl overflow-hidden animate-float-gentle flex-shrink-0"
                    style={{
                      border: "2px solid rgba(122, 201, 67, 0.15)",
                      boxShadow: "0 15px 40px rgba(0, 0, 0, 0.6), 0 0 20px rgba(122, 201, 67, 0.05)",
                      animationDelay: "-3s",
                    }}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.3, duration: 0.7 }}
                  >
                    <picture>
                      <source srcSet="/screenshot-app-restaurant.webp" type="image/webp" />
                      <img
                        src="/screenshot-app-restaurant.png"
                        alt="KumIA App - Menu digital para restaurantes"
                        className="w-full h-auto object-cover"
                        loading="lazy"
                        width={195}
                        height={395}
                      />
                    </picture>
                  </motion.div>

                  {/* Phone 2: Orders */}
                  <motion.div
                    className="w-[110px] md:w-[170px] lg:w-[195px] rounded-2xl overflow-hidden animate-float-gentle flex-shrink-0"
                    style={{
                      border: "2px solid rgba(122, 201, 67, 0.15)",
                      boxShadow: "0 15px 40px rgba(0, 0, 0, 0.6), 0 0 20px rgba(122, 201, 67, 0.05)",
                      animationDelay: "-6s",
                    }}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.5, duration: 0.7 }}
                  >
                    <picture>
                      <source srcSet="/screenshot-app-orders.webp" type="image/webp" />
                      <img
                        src="/screenshot-app-orders.png"
                        alt="KumIA App - Sistema de pedidos digitales"
                        className="w-full h-auto object-cover"
                        loading="lazy"
                        width={195}
                        height={395}
                      />
                    </picture>
                  </motion.div>
                </div>
              </div>
            </motion.div>

          </motion.div>
        </div>
      </section>

      {/* ROI Calculator Modal */}
      <ROICalculator
        language="es"
        isVisible={showROICalculator}
        onClose={() => setShowROICalculator(false)}
        onShowSignUp={onShowSignUp}
      />
    </div>
  );
};

export default FuturisticKumiaLanding;
